import { connect, NatsConnection, RequestOptions } from 'nats.ws';
import * as v from 'valibot';
import Game from './Game';
import { game_query, GameNodeSchema, location_query, LocationNodeSchema } from './graph';
import { Clock } from './lib/hlc';
import RpcClient from './lib/RpcClient';
import TimesyncClient from './lib/TimesyncClient';
import Location from './Location';

export type StClientOptions = {
	server: string;
	secure?: boolean;
}

export class StClient {
	options: StClientOptions;
	nats: NatsConnection;
	graphql_url: string;
	hlc: Clock;
	rpc: RpcClient;
	sync: TimesyncClient;

	constructor(options: Required<StClientOptions>, nats: NatsConnection) {
		this.options = options;
		this.nats = nats;
		this.graphql_url = `http${options.secure ? 's' : ''}://${options.server}/graphql`;
		this.hlc = new Clock();
		this.rpc = new RpcClient(nats);
		this.sync = new TimesyncClient(this.rpc);

		this.sync.subscribe(({ clock_offset, net_delay }) => this.hlc.offset = clock_offset + net_delay);
	}

	dispose () {
		this.sync.dispose();
	}

	static async new (options: StClientOptions) {
		if (options.secure == null) options.secure = isSecureContext;
		const nats = await connect({ servers: [`ws${options.secure ? 's' : ''}://${options.server}/nats`] });
		return new StClient(options as Required<StClientOptions>, nats);
	}

	subscribe (subject: string) {
		const subj = `st.v0.events.${subject}`;
		console.log('subscribing to', subj);
		return this.nats.subscribe(subj);
	}

	async request<T = any> (subject: string, req: any, options?: RequestOptions) {
		// console.log('request:', `st.v0.${subject}`, req, options);
		const rsp = await this.rpc.request<T>(`st.v0.${subject}`, req, options);
		// console.log('response:', rsp);
		return rsp;
	}

	async graphQuery (query: string, variables?: Record<string, any>) {
		const rsp = await fetch(this.graphql_url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ query, variables }),
		});

		const body = await rsp.json();
		if (!rsp.ok || body.errors) {
			console.error(body.errors);
			throw new Error(body.errors[0].message);
		}

		return body.data;
	}

	async loadLocation (location_id: string) {
		console.log(location_query, location_id);
		const data = await this.graphQuery(location_query, { location_id });

		try {
			v.assert(LocationNodeSchema, data.location);
		} catch (err: any) {
			if (v.isValiError(err)) console.error(v.summarize(err.issues));
			throw err;
		}

		return new Location(this, data.location);
	}

	async loadGame (game_id: string) {
		console.log(game_query, game_id);
		const data = await this.graphQuery(game_query, { game_id });

		try {
			v.assert(GameNodeSchema, data.game);
		} catch (err) {
			if (v.isValiError(err)) console.error(v.summarize(err.issues));
			throw err;
		}

		return new Game(this, data.game);
	}
}

export { StClient as default };
